
import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronRight, HelpCircle, X } from "lucide-react";
import PageTransition from "@/components/PageTransition";

const PricingToggle = ({ isAnnual, setIsAnnual }: { isAnnual: boolean; setIsAnnual: (value: boolean) => void }) => {
  return (
    <div className="flex items-center justify-center space-x-3 mb-12">
      <span className={`text-sm font-medium ${!isAnnual ? 'text-expitrack-primary' : 'text-gray-500'}`}>Monthly</span>
      <button
        onClick={() => setIsAnnual(!isAnnual)}
        className="relative inline-flex h-6 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-expitrack-primary focus:ring-offset-2"
        role="switch"
        aria-checked={isAnnual}
      >
        <span
          className={`${
            isAnnual ? 'translate-x-6' : 'translate-x-0'
          } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
        />
      </button>
      <span className={`text-sm font-medium ${isAnnual ? 'text-expitrack-primary' : 'text-gray-500'}`}>
        Annual <span className="px-2 py-0.5 text-xs bg-expitrack-primary/10 text-expitrack-primary rounded-full ml-1">Save 20%</span>
      </span>
    </div>
  );
};

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(true);
  
  const tiers = [
    {
      name: "Free",
      description: "Everything you need to start tracking expirations",
      price: { monthly: "$0", annual: "$0" },
      features: [
        "Up to 50 items",
        "Basic expiry predictions",
        "Manual data entry",
        "CSV import",
        "7-day alert window",
        "Basic dashboard",
      ],
      limitations: [
        "No AI-powered forecasting",
        "No custom categories",
        "No data export",
      ],
      cta: "Get Started",
      highlight: false,
    },
    {
      name: "Pro",
      description: "Advanced prediction and unlimited tracking",
      price: { monthly: "$9.99", annual: "$95.88" },
      features: [
        "Unlimited items",
        "AI-powered expiry predictions",
        "All data import methods",
        "Custom alert windows",
        "Advanced analytics dashboard",
        "Custom categories",
        "Data export",
        "Email notifications",
        "Priority support",
      ],
      limitations: [],
      cta: "Upgrade to Pro",
      highlight: true,
    },
  ];
  
  return (
    <PageTransition className="min-h-screen pt-24 pb-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Simple, transparent pricing</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose the plan that's right for you and start reducing waste today.
          </p>
        </div>
        
        <PricingToggle isAnnual={isAnnual} setIsAnnual={setIsAnnual} />
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {tiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              className={`bg-white rounded-2xl overflow-hidden shadow-card ${
                tier.highlight ? 'border-2 border-expitrack-primary' : 'border border-gray-100'
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
            >
              {tier.highlight && (
                <div className="bg-expitrack-primary text-white py-2 text-center text-sm font-medium">
                  Most Popular
                </div>
              )}
              <div className="p-6 md:p-8">
                <div className="flex justify-between items-start">
                  <div>
                    <h2 className="text-2xl font-bold">{tier.name}</h2>
                    <p className="text-gray-600 mt-1">{tier.description}</p>
                  </div>
                </div>
                
                <div className="mt-6">
                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold">
                      {isAnnual ? tier.price.annual : tier.price.monthly}
                    </span>
                    <span className="ml-2 text-gray-500">
                      {tier.name !== "Free" && (isAnnual ? "/year" : "/month")}
                    </span>
                  </div>
                  {isAnnual && tier.name !== "Free" && (
                    <p className="text-sm text-expitrack-primary mt-1">
                      ${(Number(tier.price.monthly.replace("$", "")) * 12 * 0.8).toFixed(2)} billed annually
                    </p>
                  )}
                </div>
                
                <div className="mt-8">
                  <Link 
                    to="/onboard"
                    className={`w-full py-3 flex items-center justify-center rounded-lg font-medium transition-all ${
                      tier.highlight 
                        ? 'bg-expitrack-primary text-white hover:bg-expitrack-primary/90 shadow-sm hover:shadow' 
                        : 'bg-white border border-gray-200 text-expitrack-text hover:bg-gray-50'
                    }`}
                  >
                    {tier.cta} <ChevronRight className="ml-1 w-4 h-4" />
                  </Link>
                </div>
                
                <div className="mt-8">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">What's included:</h3>
                  <ul className="space-y-3">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-expitrack-primary mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {tier.limitations.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-gray-900 mb-4">Limitations:</h3>
                      <ul className="space-y-3">
                        {tier.limitations.map((limitation, index) => (
                          <li key={index} className="flex items-start">
                            <X className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{limitation}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
        
        <div className="mt-16 max-w-3xl mx-auto bg-white rounded-xl p-6 md:p-8 shadow-sm">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <HelpCircle className="w-6 h-6 text-expitrack-primary" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold mb-2">Why upgrade to Pro?</h3>
              <p className="text-gray-600 mb-4">
                Our Pro plan offers advanced AI-powered prediction algorithms that improve over time, 
                learning from your specific patterns to provide more accurate expiry forecasts.
              </p>
              <p className="text-gray-600">
                Pro users report an additional 15% reduction in waste compared to Free users,
                resulting in greater cost savings and environmental benefits.
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4">Have questions?</h3>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Contact our team for personalized help or to discuss enterprise options
            for large organizations and specific industry needs.
          </p>
          <Link 
            to="/contact" 
            className="px-6 py-3 bg-white border border-gray-200 rounded-lg text-expitrack-primary font-medium hover:bg-gray-50 transition-colors inline-flex items-center"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    </PageTransition>
  );
};

export default Pricing;
